<template>
	<v-dialog v-model="visible" :max-width="maxWidth">
		<v-card :loading="loading">
			<layout-error-boundary
				ref="errBoundary"
				v-slot="{ hasError, err }"
				:on-error="handleFormError"
			>
				<v-card-title class="headline" v-text="strings.title" />
				<v-card-text v-if="strings.body" v-text="strings.body" />

				<v-card-text v-if="hasError">
					<v-alert
						type="error"
						border="left"
						dismissible
						outlined
						@click.native="$refs.errBoundary.resetError()"
					>
						<span class="error--text">
							{{ errorMessage(err) }}
						</span>
					</v-alert>
				</v-card-text>

				<v-form
					ref="form"
					autocomplete="off"
					lazy-validation
					@submit.prevent="submit"
				>
					<v-card-text>
						<slot />
					</v-card-text>

					<v-card-actions>
						<v-btn
							v-if="!!handleDelete"
							color="rust-red"
							text
							@click="onDelete"
							v-text="strings.delete || 'Delete'"
						/>
						<v-spacer />
						<v-btn
							color="ocean darken-1"
							text
							@click="visible = false"
							v-text="strings.cancel"
						/>
						<v-btn
							color="ocean darken-1"
							type="submit"
							outlined
							v-text="strings.submit"
						/>
					</v-card-actions>
				</v-form>
			</layout-error-boundary>
		</v-card>
	</v-dialog>
</template>

<script>
import { APIError, APIAuthError } from "@/utils/http";

const initData = () => ({
	visible: false,
	loading: false,
	error: {
		hasError: false,
		err: null,
	},

	resolve: null,
	handleSubmit: null,
	handleDelete: null,

	payload: {},
});

export default {
	props: {
		strings: { type: Object, required: true },
		maxWidth: { type: Number, required: false, default: 360 },
	},

	data: initData,

	watch: {
		visible(is, was) {
			// Reset all relevant data whenever dialog closes
			if (was && !is) this.reset();
		},
	},

	methods: {
		reset() {
			Object.assign(this, initData());
			this.$emit("reset");
			this.$refs.errBoundary.resetError();
			this.$refs.form.resetValidation();
		},

		pop(handleSubmit, handleDelete) {
			this.visible = true;
			this.handleSubmit = handleSubmit;
			this.handleDelete = handleDelete;
			return new Promise(resolve => (this.resolve = resolve));
		},

		updateFormData(payload) {
			this.payload = payload;
		},

		async submit() {
			const valid = this.$refs.form.validate();
			if (!valid) return;

			this.loading = true;

			try {
				let resp = this.payload;
				if (this.handleSubmit) {
					resp = await this.handleSubmit(resp);
				}
				if (this.resolve) {
					this.resolve(resp);
				}
				this.visible = false;
			} finally {
				this.loading = false;
			}
		},

		async onDelete() {
			this.loading = true;
			try {
				if (this.handleDelete) await this.handleDelete();
				this.visible = false;
			} catch (e) {
				e.isDeleteError = true;
				throw e;
			} finally {
				this.loading = false;
			}
		},

		errorMessage(err) {
			const preamble = err.isDeleteError
				? this.strings.deleteError
				: this.strings.error;
			const message = err.message ? `: ${err.message}` : "";
			return `${preamble}${message}`;
		},

		handleFormError(err) {
			// We only want to handle certain types of errors. Keep propagating
			// any others.
			const isApiErr =
				err instanceof APIError && !(err instanceof APIAuthError);
			return !isApiErr;
		},
	},
};
</script>
